import React, { useEffect } from 'react';

import {
  Box,
  Button,
  FormField,
  Loader,
  MenuItem,
  PageHeader,
  Typography,
} from 'components';
import { useNotifications } from 'hooks';

import { FieldError, useForm, useSelector, useTranslation } from 'third-party';

import { useUser } from 'modules/user';

import { userSelectors } from 'selectors';

import { MultiSelectDropdown } from 'components/MultiSelectDropdown/MultiSelectDropdown';

import { camelCaseHumanized } from 'utils/app';
import { getEnumValues } from 'utils/helper';

import { APIUser, APIUserRight, APIUserType } from 'types/api';

enum FormFields {
  userType = 'userType',
  rights = 'rights',
}
type FormData = {
  [FormFields.userType]: APIUserType;
  [FormFields.rights]: string[];
};

type CreateEditProps = {
  tenantId: string;
  onModalClose: () => void;
  userId?: string | null;
};

export const UserRightsEditForm: React.FC<CreateEditProps> = ({
  userId,
  tenantId,
  onModalClose,
}) => {
  const { loadUser, clearUser } = useUser();

  const user = useSelector(userSelectors.user);

  useEffect(() => {
    if (userId) {
      loadUser(userId);
    }
    return () => {
      clearUser();
    };
  }, [userId, loadUser, clearUser]);

  if (!user) {
    return <Loader />;
  }

  return (
    <CreateEditForm
      user={user}
      tenantId={tenantId}
      onModalClose={onModalClose}
    />
  );
};

type Props = {
  tenantId: string;
  user: APIUser | null;
  onModalClose: () => void;
};

const selectOptions = (enumObject: Record<string, unknown>) => {
  return Object.keys(enumObject).map((option: string) => (
    <MenuItem key={option} value={option}>
      <Typography variant="body2">{option}</Typography>
    </MenuItem>
  ));
};

export const CreateEditForm: React.FC<Props> = ({
  user,
  tenantId,
  onModalClose,
}) => {
  const { error, success } = useNotifications();
  const { updateUserRights } = useUser();
  const { t } = useTranslation();

  const userTenant = user?.userTenants.find(
    it => it.tenant?.id === tenantId,
  );

  const {
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      [FormFields.userType]: userTenant?.userType || APIUserType.User,
      [FormFields.rights]:
        userTenant?.userRights.map(it => it.right.toString()) || [],
    },
  });

  const isFormPending = false;
  const userType = watch(FormFields.userType);

  useEffect(() => {
    if (user && userType == APIUserType.User) {
      setValue(FormFields.rights, []);
    }
    if (user && userType == APIUserType.Admin) {
      setValue(FormFields.rights, getEnumValues(APIUserRight));
    }
  }, [user, userType, setValue]);

  const handleCancel = () => {
    onModalClose();
  };

  const onSubmit = async (formData: FormData) => {
    if (user) {
      const result = await updateUserRights(user?.id, formData as any);
      if (result.error) {
        error(result.payload.body?.title || t('userForm.edit.error'));
      } else {
        success(t('userForm.edit.sucscefull'));
        onModalClose();
      }
      return;
    }
  };

  return (
    <Box padding="1.5rem">
      <form>
        <PageHeader
          header={t('userForm.editRight.header')}
          subHeader={t('userForm.editRight.subHeader')}
        >
          <Box display="flex" columnGap="1rem">
            <Button
              type="submit"
              variant="outlined"
              onClick={handleSubmit(onSubmit)}
              size="medium"
              color="primary"
            >
              {t('common.form.save')}
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={handleCancel}
              size="medium"
              color="error"
            >
              {t('common.form.cancel')}
            </Button>
          </Box>
        </PageHeader>
        <FormField
          fieldName={FormFields.userType}
          fieldError={errors[FormFields.userType] as FieldError}
          label={t('membersList.column.userType')}
          placeholder={
            t('common.form.enterValueFor') + t('membersList.column.userType')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={200}
          selectOptions={selectOptions(APIUserType)}
          isRequired
          isDisabled={isFormPending}
        />
        <MultiSelectDropdown
          fieldName={FormFields.rights}
          control={control}
          isDisabled={isFormPending || userType !== APIUserType.Editor}
          caption={'User access'}
          defaultSelected={userTenant?.userRights.map(it =>
            it.right.toString(),
          )}
          options={getEnumValues(APIUserRight).map(it => ({
            key: it.toString(),
            value: camelCaseHumanized(it),
          }))}
        />
      </form>
      <Typography variant="body2" color="textSecondary" marginTop="1rem">
        {t('userForm.helpText')}
      </Typography>
    </Box>
  );
};
